// Central registry of survey panels. A panel is a traffic source (Cint,
// Dynata, TapResearch, QuestionPro) identified by a URL query param and, for
// some, an external completion redirect + idle-termination rejection flow.
//
// To add a panel: add ONE entry here (+ a tap_connect_users column and a case
// in src/server/panelUrls.ts if it needs completion/termination URLs). The
// derivation, client redirect gate, idle-poll enablement, and idle-termination
// SQL all read from this registry, so they stay in sync automatically.

export type PanelId =
  'cint' | 'dynata' | 'tap_research' | 'questionpro' | 'listen_labs';

export type PanelDef = {
  id: PanelId;
  // URL query param that identifies this panel. Also the tap_connect_users
  // column name where the identifier is stored (rid, psid, tid).
  param: string;
  // Fires the idle-termination rejection callback when the user abandons
  // before completing.
  idleTerminates: boolean;
  // Logo shown in the app header when this panel is active. No logo shown
  // when omitted (including when no panel is detected at all).
  logoUrl?: string;
  // Hides the app header entirely for this panel (no logo, branded or not).
  hideHeader?: boolean;
};

// Order encodes derivation precedence: the first panel whose param is present
// wins (matches the historical rid > psid > tid > panelId ordering).
export const PANELS: PanelDef[] = [
  { id: 'cint', param: 'rid', idleTerminates: true, hideHeader: true },
  { id: 'dynata', param: 'psid', idleTerminates: true, hideHeader: true },
  {
    id: 'tap_research',
    param: 'tid',
    idleTerminates: false,
    logoUrl: '/logos/tap-logo.png',
  },
  {
    id: 'questionpro',
    param: 'panelId',
    idleTerminates: false,
    logoUrl: '/logos/questionpro-logo.png',
  },
  {
    id: 'listen_labs',
    param: 'participant_id',
    idleTerminates: false,
    hideHeader: true,
  },
];

export const IDLE_TERMINATION_PANELS = PANELS.filter((p) => p.idleTerminates);

// Panels that redirect to an external completion URL by presence of their
// param. QuestionPro is token-driven (handled via questionProTaskStatus /
// questionPro.getRedirectUrlIfAny) and excluded from param-presence gates.
// Listen Labs has a static completion URL (see panelCompletionUrl) and needs
// a studyId to build it — panelCompletionUrl returns null without one, so an
// incomplete Listen Labs param set just no-ops the redirect.
const NO_PARAM_COMPLETION_PANELS: PanelId[] = ['questionpro'];

const EXTERNAL_COMPLETION_PANELS = PANELS.filter(
  (p) => !NO_PARAM_COMPLETION_PANELS.includes(p.id)
);

// Resolve the panel id from a bag of params (query params or session fields).
export function derivePanelId(params: Record<string, unknown>): PanelId | null {
  return PANELS.find((p) => params[p.param])?.id ?? null;
}

// True when any external-completion panel's param is present — i.e. the user
// should be redirected back to the panel on success.
export function hasExternalPanelParam(
  params: Record<string, unknown>
): boolean {
  return EXTERNAL_COMPLETION_PANELS.some((p) => !!params[p.param]);
}
